import moment from 'moment'

import type Leave from 'modules/timeoff/store/models/Leave'

export const LEAVE_UNITS_TYPES = Object.freeze({
  hours: 'hours',
  days: 'days',
})

const leaveAccruesAndUseNaturalDays = (
  leave: Leave,
  employeeTimeoffPolicyId: string | null | undefined
) => {
  const allowance =
    leave.leaveType && leave.leaveType.getAllowanceForEmployee(employeeTimeoffPolicyId)
  const naturalDays = Boolean(
    allowance && (allowance.natural || allowance.frenchOuvre || allowance.frenchCalendarDays)
  )

  return leave.attributes.accrues && naturalDays
}

const leaveIncludeSmRestDays = (
  leave: Leave,
  employeeTimeoffPolicyId: string | null | undefined
) => {
  const allowance =
    leave.leaveType && leave.leaveType.getAllowanceForEmployee(employeeTimeoffPolicyId)
  return allowance?.countSmRestDays
}

export const leaveIsHourly = (leave: Leave, employeeTimeoffPolicyId: string | null | undefined) => {
  // Priority 1: Check leave type allowance configuration for the employee.
  // This is the most reliable source when available.
  const allowanceForEmployee = leave.leaveType?.getAllowanceForEmployee(employeeTimeoffPolicyId)

  if (allowanceForEmployee) {
    return allowanceForEmployee.isHourly
  }

  // Priority 2: Check the REST API's allowance_units field if available
  // This is more reliable than inferring from hourly data fields
  if (leave.attributes.allowance_units) {
    return leave.attributes.allowance_units === 'hours'
  }

  // Fallback: In very rare cases the allowance is not being properly loaded.
  // Check if the leave has hourly data (start_time and hours amount).
  // This is unreliable for corrupted data but kept as last resort.
  const hasHoursAmount = !!(leave.attributes.hours_amount || leave.attributes.hours_amount_in_cents)
  const isHourlyForLeave = !!(leave.attributes.start_time && hasHoursAmount)

  return isHourlyForLeave
}

/**
 * This method uses different fields to
 * set finish_on of a leave. Is dependend of
 *   1. Policy (if use natural_days)
 *   2. If this leave accrues
 */
export const leaveFinishOn = (leave: Leave, employeeTimeoffPolicyId: string | null | undefined) => {
  const startOn = moment(leave.attributes.start_on)
  const allowEndlessAndNoFinishOn =
    leave.leaveType?.attributes.allow_endless && !leave.attributes.finish_on
  const isStartOnGreaterThanToday = startOn.isAfter(moment())
  const finishOn =
    leaveAccruesAndUseNaturalDays(leave, employeeTimeoffPolicyId) ||
    leaveIncludeSmRestDays(leave, employeeTimeoffPolicyId)
      ? 'finish_on_natural'
      : 'finish_on'

  if (allowEndlessAndNoFinishOn && !isStartOnGreaterThanToday) {
    return moment()
  }

  if (allowEndlessAndNoFinishOn && isStartOnGreaterThanToday) {
    return startOn
  }

  return moment(leave.attributes[finishOn])
}

export const leaveUnits = (leave: Leave, employeeTimeoffPolicyId: string | null | undefined) => {
  const type = leaveIsHourly(leave, employeeTimeoffPolicyId)
    ? LEAVE_UNITS_TYPES.hours
    : LEAVE_UNITS_TYPES.days

  const units = leave.leaveType?.isWorkable
    ? leave.attributes.workable_units
    : leave.attributes.used_units

  return units[type]
}

export const leaveTotalDaysOrHours = (
  leave: Leave,
  employeeTimeoffPolicyId: string | null | undefined
) => {
  const units = leaveUnits(leave, employeeTimeoffPolicyId)

  if (units === undefined) {
    return 0
  }

  return Object.keys(units)
    .map((year) => units[year])
    .reduce<number>((a, b) => {
      if (b !== undefined) {
        return a + b
      }

      return a
    }, 0)
}

/**
 * This method makes sure the finish date is valid.
 * If not valid, finish date is set to:
 *   1. today if start date is in the past
 *   2. start date if equals to today or in the future
 * This is useful for leaves without an end date.
 */
export const leaveValidFinishOn = (
  leave: Leave,
  employeeTimeoffPolicyId: string | null | undefined
) => {
  const finishOn = leaveFinishOn(leave, employeeTimeoffPolicyId)
  if (finishOn.isValid()) return finishOn

  const start = moment(leave.attributes.start_on)
  return leave.sickLeaveEnd(start)
}
